import type * as React from 'react';
import { get } from '@Domain/Api';
import usersFetched, {
	type UsersFetchedAction,
	type UsersListFetchedPayload,
} from '@Application/Users/Users/Actions/usersFetched';
import usersFetchFailed, {
	type UsersFetchFailedAction,
} from '@Application/Users/Users/Actions/usersFetchFailed';
import type FetchFacilityUsers from '@Application/Users/Users/Queries/FetchFacilityUsers';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';
import parameters from '../../../parameters';

type Actions = UsersFetchedAction | UsersFetchFailedAction;

/**
 * Deliberate homonym of `Application/FacilityUsers/fetchFacilityUsers.ts`: both call the same route,
 * `GET /api/facilities/{facilityId}/users`, but write to different store locations. This one feeds
 * `usersStore.users`, the administration screen's server-filtered, server-paginated page. The other
 * feeds `facilityUsersStore.users`, the beneficiary dossier's own unfiltered agency directory. The
 * two must stay apart: `UsersListView` carries only `accountId` as its ownership key, so a write from
 * the other fetch here — same account, different facility scope — would look like the same page to
 * this screen's `stopOn` and would silently replace what it shows.
 */
export default (query: FetchFacilityUsers) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			// Server-side pagination: `get` is used deliberately, never `getPaginated` (which walks every
			// page and would make `totalRecords` lie to the paginator).
			const baseUrl = `${parameters.api.host}/api/facilities/${query.facilityId}/users`;
			const queryString = query.toQueryString();
			const url = queryString.length > 0 ? `${baseUrl}?${queryString}` : baseUrl;

			const payload = await get<UsersListFetchedPayload>(url);

			const action = usersFetched(payload, query.accountId);
			dispatch(action);
			return action;
		} catch (error) {
			const action = usersFetchFailed(extractMessages(error));
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
